import { fetchListingDetailV3 } from './listing';

export type ApartmentReviews = {
  reputation?: {
    averageRating?: number;
    totalReviews?: number;
    categories?: Array<{ category: string; summary?: string; reviewCount?: number; score?: number }>;
  };
  google?: { placeId: string };
  reddit?: { links: Array<{ topic: string; url: string }> };
};

export type BuildingReviewsSnapshot = {
  lotId: string;
  reviews: ApartmentReviews | null;
  updatedAt: number;
};

export const APARTMENT_REVIEWS_TIMEOUT_MS = 1_500;
export const APARTMENT_REVIEWS_STALE_TIME_MS = 5 * 60 * 1000;

const record = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const count = (value: unknown): value is number => finite(value) && Number.isInteger(value) && value >= 0;
const nonempty = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

export function normalizeApartmentReviews(value: unknown): ApartmentReviews | null {
  const raw = record(value);
  if (!raw) return null;
  const reviews: ApartmentReviews = {};
  const reputation = record(raw.reputation);
  if (reputation) {
    const categories = Array.isArray(reputation.categories) ? reputation.categories.flatMap((item: unknown) => {
      const category = record(item);
      if (!category || !nonempty(category.category)) return [];
      return [{
        category: category.category,
        ...(nonempty(category.summary) ? { summary: category.summary } : {}),
        ...(count(category.reviewCount) ? { reviewCount: category.reviewCount } : {}),
        ...(finite(category.score) ? { score: category.score } : {}),
      }];
    }) : [];
    const averageRating = reputation.averageRating;
    const totalReviews = reputation.totalReviews;
    const validRating = finite(averageRating) && averageRating >= 0 && averageRating <= 5;
    if (validRating || count(totalReviews) || categories.length) {
      reviews.reputation = {
        ...(validRating ? { averageRating } : {}),
        ...(count(totalReviews) ? { totalReviews } : {}),
        ...(categories.length ? { categories } : {}),
      };
    }
  }
  const google = record(raw.google);
  if (nonempty(google?.placeId)) reviews.google = { placeId: google.placeId.trim() };
  const reddit = record(raw.reddit);
  const links = Array.isArray(reddit?.links) ? reddit.links.flatMap((item: unknown) => {
    const link = record(item);
    if (!link || !nonempty(link.topic) || !nonempty(link.url)) return [];
    try {
      const url = new URL(link.url);
      if (url.protocol !== 'https:' || !['reddit.com', 'www.reddit.com'].includes(url.hostname) || url.username || url.password) return [];
      return [{ topic: link.topic, url: link.url }];
    } catch { return []; }
  }) : [];
  if (links.length) reviews.reddit = { links };
  return Object.keys(reviews).length ? reviews : null;
}

export function isApartmentReviewsEligible(listing: {
  propertyType?: string;
  active?: boolean;
  isAllowedToRetain?: boolean;
}): boolean {
  return ['large', 'medium', 'garden'].includes(listing.propertyType?.toLowerCase() ?? '') &&
    (listing.active !== false || listing.isAllowedToRetain !== false);
}

export async function fetchBuildingReviews(lotId: string, signal?: AbortSignal): Promise<ApartmentReviews | null> {
  const detail = await fetchListingDetailV3(lotId, { signal, timeout: APARTMENT_REVIEWS_TIMEOUT_MS });
  return normalizeApartmentReviews(detail.reviews);
}
